// components/InspirationSlidePanel.js
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/solid";
import apiClient from "../utils/apiClient";
import { useNavigate } from "react-router-dom";

const CUISINES = [
  "African", "American", "British", "Caribbean", "Central European", "Chinese",
  "Eastern European", "French", "Greek", "Indian", "Italian", "Japanese",
  "Korean", "Latin American", "Mediterranean", "Mexican", "Middle Eastern",
  "Nordic", "South American", "Southeast Asian", "Spanish", "Thai", "Turkish",
  "Vietnamese", "Fusion", "Other"
];

const TIME_OPTIONS = [
  { label: "Under 30 mins", value: "under30" },
  { label: "Under 45 mins", value: "under45" },
  { label: "Under 1 hour", value: "under60" },
  { label: "Over 1 hour", value: "over60" },
];

export default function InspirationSlidePanel({ isOpen, onClose }) {
  const [cuisine, setCuisine] = useState("");
  const [time, setTime] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const addIngredient = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !ingredients.includes(trimmed)) {
      setIngredients([...ingredients, trimmed]);
      setInputValue("");
    }
  };

  const removeIngredient = (item) => {
    setIngredients(ingredients.filter((i) => i !== item));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // Build the initial message for Gemini
    let prompt = "Give me some recipe ideas.";
    if (cuisine) prompt += ` Cuisine: ${cuisine}.`;
    if (time) {
      const label = TIME_OPTIONS.find((t) => t.value === time)?.label;
      prompt += ` Time: ${label}.`;
    }
    if (ingredients.length > 0) {
      prompt += ` Must include: ${ingredients.join(", ")}.`;
    }

    try {
      // Start new conversation (no conversationId)
      const res = await apiClient.post(
        "/chat",
        {
          messages: [{ role: "user", content: prompt }],
          conversationId: null,
          inspireMe: true
        },
        { params: { responseVersion: "v0" } }
      );

      const conversationId = res.data.conversationId;
      // clear local chat state so ChatPage loads fresh
      localStorage.removeItem("chatMessages");
      localStorage.setItem("chatConversationId", conversationId);

      navigate(`/chat/${conversationId}`);
      onClose();
    } catch (err) {
      console.error("Error starting inspiration chat:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex justify-center items-end md:items-center bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full md:max-w-2xl rounded-t-2xl md:rounded-b-2xl md:max-h-[90%] max-h-[90vh] bg-white overflow-y-auto relative flex flex-col"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(e, info) => {
              if (info.offset.y > 100) onClose();
            }}
            style={{
                paddingBottom: "calc(var(--safe-area-inset-bottom) )", // safe area   + 0.75rem
              }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 p-4 pb-0">
              <div className="flex justify-center mb-2 pb-4 md:hidden">
                <div className="w-10 h-1 rounded bg-gray-300"></div>
              </div>
              <div className="hidden md:flex justify-end px-4">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-black text-2xl"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <h2 className="font-semibold text-lg px-2">
                Inspire me
              </h2>
            </div>

            {/* Content */}
            <div className="px-4 py-4 space-y-6 overflow-y-auto flex-1">
              {/* Cuisine */}
              <div>
                <label className="block text-sm font-medium mb-1">Cuisine</label>
                <select
                  value={cuisine}
                  onChange={(e) => setCuisine(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                >
                  <option value="">Any</option>
                  {CUISINES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium mb-1">Time to cook</label>
                <div className="space-y-2">
                  {TIME_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="time"
                        value={opt.value}
                        checked={time === opt.value}
                        onChange={() => setTime(opt.value)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Must include ingredients
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addIngredient();
                      }
                    }}
                    className="flex-1 border rounded p-2 text-sm"
                    placeholder="e.g. chicken, garlic"
                  />
                  <button
                    type="button"
                    onClick={addIngredient}
                    className="bg-sage text-white px-3 rounded text-sm"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ingredients.map((item) => (
                    <span
                      key={item}
                      className="bg-gray-200 text-sm px-2 py-1 rounded-full flex items-center gap-1"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => removeIngredient(item)}
                        className="text-gray-600 hover:text-red-500"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-sage text-white py-2 rounded-md hover:bg-sagedark disabled:opacity-50"
              >
                {isSubmitting ? "Cooking up something special..." : "Let's go"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
